/**
 * HiiKER Land Access Overlay — Mapbox GL JS Integration
 * ======================================================
 * US public land access data (USGS PAD-US 4.1) as a toggleable map overlay.
 *
 * Access types:
 *   open       → green        (#4CAF50)
 *   fee        → blue         (#2196F3)
 *   permit     → orange       (#FF9800)
 *   limited    → purple       (#AB47BC)
 *   restricted → deep orange  (#FF5722)
 *   closed     → red          (#F44336)
 *   unknown    → grey         (#9E9E9E)
 *
 * Seasonal areas use lighter colour variants + diagonal stripe overlay.
 * Hunting areas use orange hatch pattern + bold orange border.
 */

import mapboxgl from 'mapbox-gl';
import { MapLayerCategory } from '../../../utils/layer-registry';
import { skipOnHazard } from '../../../utils/trailHazards/hazardLayerSpec';
import { i18n } from '../../../i18n';

const t = (key: string, values?: Record<string, unknown>): string =>
  values ? i18n.global.t(key, values) : i18n.global.t(key);

// ── Constants ───────────────────────────────────────────────────────────────

const TILESET_ID = 'hiiker-maps.hiiker-land-access';
const SOURCE_ID = 'land-access';
const SOURCE_LAYER = 'land_access';

const LAYER_ID = 'land-access';

export const ALL_LAND_ACCESS_LAYER_IDS = [
  LAYER_ID,
  `${LAYER_ID}-seasonal`,
  `${LAYER_ID}-seasonal-pattern`,
  `${LAYER_ID}-hunting-pattern`,
  `${LAYER_ID}-hunting-outline`,
  `${LAYER_ID}-outline`,
  `${LAYER_ID}-labels`,
];

// ── Legend data (exported for UI rendering) ─────────────────────────────────

export const LAND_ACCESS_LEGEND = [
  { access: 'open',       color: '#2E7D32', seasonalColor: '#A5D6A7', get label() { return t('base_map_dialog.land_access_overlay.open_access'); }, get description() { return t('base_map_dialog.land_access_overlay.open_access_description'); } },
  { access: 'fee',        color: '#2196F3', seasonalColor: '#90CAF9', get label() { return t('base_map_dialog.land_access_overlay.entry_fee'); }, get description() { return t('base_map_dialog.land_access_overlay.entry_fee_description'); } },
  { access: 'permit',     color: '#FF9800', seasonalColor: '#FFCC80', get label() { return t('base_map_dialog.land_access_overlay.permit_required'); }, get description() { return t('base_map_dialog.land_access_overlay.permit_required_description'); } },
  { access: 'limited',    color: '#AB47BC', seasonalColor: '#CE93D8', get label() { return t('base_map_dialog.land_access_overlay.limited_access'); }, get description() { return t('base_map_dialog.land_access_overlay.limited_access_description'); } },
  { access: 'restricted', color: '#FF5722', seasonalColor: '#FFAB91', get label() { return t('base_map_dialog.land_access_overlay.restricted'); }, get description() { return t('base_map_dialog.land_access_overlay.restricted_description'); } },
  { access: 'closed',     color: '#F44336', seasonalColor: '#EF9A9A', get label() { return t('base_map_dialog.land_access_overlay.closed'); }, get description() { return t('base_map_dialog.land_access_overlay.closed_description'); } },
  { access: 'unknown',    color: '#9E9E9E', seasonalColor: '#E0E0E0', get label() { return t('base_map_dialog.land_access_overlay.unknown'); }, get description() { return t('base_map_dialog.land_access_overlay.unknown_description'); } },
];

// ── Popup CSS (injected dynamically so it's available on any page) ──────────

const POPUP_STYLE_ID = 'land-access-popup-styles';

const POPUP_CSS = `
.hiiker-land-popup .mapboxgl-popup-content {
  background: rgba(23, 24, 29, 0.95);
  backdrop-filter: blur(8px);
  border-radius: 10px;
  padding: 0;
  color: #fff;
  box-shadow: 0 4px 20px rgba(0,0,0,0.35);
  max-width: 340px;
}
.hiiker-land-popup .mapboxgl-popup-tip {
  border-top-color: rgba(23, 24, 29, 0.95);
}
.hiiker-land-popup .mapboxgl-popup-close-button {
  color: #fff;
  font-size: 18px;
  padding: 4px 8px;
  opacity: 0.7;
}
.hiiker-land-popup .mapboxgl-popup-close-button:hover {
  opacity: 1;
  background: transparent;
}
.la-popup {
  padding: 18px 16px 12px;
  font-size: 13px;
  line-height: 1.45;
}
.la-popup-header { margin-bottom: 8px; }
.la-popup-name { font-size: 15px; font-weight: 700; margin-bottom: 2px; }
.la-popup-meta { font-size: 12px; color: rgba(255,255,255,0.6); }
.la-badges { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; }
.la-badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; color: #fff; }
.la-info-row { padding: 4px 0; font-size: 12px; display: flex; align-items: baseline; gap: 6px; }
.la-icon { flex-shrink: 0; }
.la-permit-link { color: #90CAF9; text-decoration: none; margin-left: 4px; }
.la-permit-link:hover { text-decoration: underline; }
.la-gap-detail { font-size: 11px; color: rgba(255,255,255,0.65); margin: 6px 0; padding: 6px 8px; background: rgba(255,255,255,0.06); border-radius: 4px; border-left: 3px solid rgba(255,255,255,0.3); }
.la-info-link { display: inline-block; margin-top: 6px; color: #90CAF9; font-size: 12px; text-decoration: none; }
.la-info-link:hover { text-decoration: underline; }
.la-popup-footer { margin-top: 8px; padding-top: 6px; border-top: 1px solid rgba(255,255,255,0.1); font-size: 10px; color: rgba(255,255,255,0.4); }
@media (max-width: 600px) {
  .hiiker-land-popup .mapboxgl-popup-content { max-width: calc(100vw - 40px); }
}
`;

function _injectPopupStyles(): void {
  if (document.getElementById(POPUP_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = POPUP_STYLE_ID;
  style.textContent = POPUP_CSS;
  document.head.appendChild(style);
}

function _removePopupStyles(): void {
  const el = document.getElementById(POPUP_STYLE_ID);
  if (el) el.remove();
}

// ── Internal filter state ───────────────────────────────────────────────────

const _visibleTypes = new Set(['open', 'fee', 'permit', 'limited', 'restricted', 'closed', 'unknown']);
let _showSeasonal = true;
let _showHunting = true;
let _popupInstance: mapboxgl.Popup | null = null;

// ── Page detection ──────────────────────────────────────────────────────────

function _isPlannerPage(): boolean {
  return window.location.pathname.includes('hike-planner');
}

// ── Click handler refs (for cleanup) ────────────────────────────────────────

const _clickHandlers: Array<{ layer: string; handler: (e: any) => void }> = [];
const _cursorHandlers: Array<{ layer: string; enter: () => void; leave: () => void }> = [];

// ── Add source & all layers ─────────────────────────────────────────────────

export function addLandAccess(): void {
  console.log('[LandAccess] addLandAccess called', { map: !!window.map, registry: !!window.layerRegistry });
  if (!window.map || !window.layerRegistry) return;

  if (!_isPlannerPage()) {
    _injectPopupStyles();
  }

  // Add vector source
  window.layerRegistry.addSource(SOURCE_ID, {
    type: 'vector',
    url: `mapbox://${TILESET_ID}`,
  });
  console.log('[LandAccess] Source added:', SOURCE_ID, '| exists:', !!window.map.getSource(SOURCE_ID));

  // Create canvas patterns (hasImage checks inside prevent duplicates)
  _addCanvasPatterns();

  // Layer 1 — Non-seasonal fill
  window.layerRegistry.addLayer(
    {
      id: LAYER_ID,
      type: 'fill',
      source: SOURCE_ID,
      'source-layer': SOURCE_LAYER,
      filter: ['!=', ['get', 'is_seasonal'], 1],
      layout: {
        'fill-sort-key': ['coalesce', ['get', 'render_priority'], 0],
      },
      paint: {
        'fill-color': [
          'match', ['get', 'access'],
          'open',       '#2E7D32',
          'fee',        '#2196F3',
          'permit',     '#FF9800',
          'limited',    '#AB47BC',
          'restricted', '#FF5722',
          'closed',     '#F44336',
          /* default */ '#9E9E9E',
        ],
        'fill-opacity': [
          'interpolate', ['linear'], ['zoom'],
          4, 0.25, 8, 0.40, 12, 0.55, 15, 0.60,
        ],
      },
    } as any,
    MapLayerCategory.Overlays,
    { z: 20 }
  );
  console.log('[LandAccess] Layer 1 added:', LAYER_ID, '| exists:', !!window.map.getLayer(LAYER_ID));

  // Layer 2 — Seasonal fill (lighter colours)
  window.layerRegistry.addLayer(
    {
      id: `${LAYER_ID}-seasonal`,
      type: 'fill',
      source: SOURCE_ID,
      'source-layer': SOURCE_LAYER,
      filter: ['==', ['get', 'is_seasonal'], 1],
      layout: {
        'fill-sort-key': ['coalesce', ['get', 'render_priority'], 0],
      },
      paint: {
        'fill-color': [
          'match', ['get', 'access'],
          'open',       '#A5D6A7',
          'fee',        '#90CAF9',
          'permit',     '#FFCC80',
          'limited',    '#CE93D8',
          'restricted', '#FFAB91',
          'closed',     '#EF9A9A',
          /* default */ '#E0E0E0',
        ],
        'fill-opacity': [
          'interpolate', ['linear'], ['zoom'],
          4, 0.20, 8, 0.35, 12, 0.55, 15, 0.60,
        ],
      },
    } as any,
    MapLayerCategory.Overlays,
    { z: 21 }
  );

  // Layer 3 — Seasonal stripe pattern
  window.layerRegistry.addLayer(
    {
      id: `${LAYER_ID}-seasonal-pattern`,
      type: 'fill',
      source: SOURCE_ID,
      'source-layer': SOURCE_LAYER,
      filter: ['==', ['get', 'is_seasonal'], 1],
      paint: {
        'fill-pattern': 'seasonal-stripe',
        'fill-opacity': ['interpolate', ['linear'], ['zoom'], 6, 0.4, 12, 0.6],
      },
    } as any,
    MapLayerCategory.Overlays,
    { z: 22 }
  );

  // Layer 4 — Hunting pattern
  window.layerRegistry.addLayer(
    {
      id: `${LAYER_ID}-hunting-pattern`,
      type: 'fill',
      source: SOURCE_ID,
      'source-layer': SOURCE_LAYER,
      filter: ['==', ['get', 'hunting_land'], 1],
      paint: {
        'fill-pattern': 'hunting-hatch',
        'fill-opacity': ['interpolate', ['linear'], ['zoom'], 6, 0.5, 12, 0.7],
      },
    } as any,
    MapLayerCategory.Overlays,
    { z: 23 }
  );

  // Layer 5 — Hunting outline
  window.layerRegistry.addLayer(
    {
      id: `${LAYER_ID}-hunting-outline`,
      type: 'line',
      source: SOURCE_ID,
      'source-layer': SOURCE_LAYER,
      filter: ['==', ['get', 'hunting_land'], 1],
      paint: {
        'line-color': '#E65100',
        'line-width': ['interpolate', ['linear'], ['zoom'], 7, 1.5, 13, 3],
        'line-opacity': 0.85,
      },
    } as any,
    MapLayerCategory.Overlays,
    { z: 24 }
  );

  // Layer 6 — General outline (non-hunting)
  window.layerRegistry.addLayer(
    {
      id: `${LAYER_ID}-outline`,
      type: 'line',
      source: SOURCE_ID,
      'source-layer': SOURCE_LAYER,
      filter: ['!=', ['get', 'hunting_land'], 1],
      paint: {
        'line-color': '#ffffff',
        'line-width': 0.5,
        'line-opacity': 0.4,
      },
    } as any,
    MapLayerCategory.Overlays,
    { z: 25 }
  );

  // Layer 7 — Labels (z9+)
  window.layerRegistry.addLayer(
    {
      id: `${LAYER_ID}-labels`,
      type: 'symbol',
      source: SOURCE_ID,
      'source-layer': SOURCE_LAYER,
      minzoom: 11,
      layout: {
        'text-field': ['format',
          ['get', 'name'], { 'font-scale': 1.0, 'text-font': ['literal', ['DIN Pro Bold', 'Arial Unicode MS Bold']] },
          '\n', {},
          ['match', ['get', 'access'],
            'open', t('base_map_dialog.land_access_overlay.open_access'), 'fee', t('base_map_dialog.land_access_overlay.entry_fee'), 'permit', t('base_map_dialog.land_access_overlay.permit_required'),
            'limited', t('base_map_dialog.land_access_overlay.limited_access'), 'restricted', t('base_map_dialog.land_access_overlay.restricted'),
            'closed', t('base_map_dialog.land_access_overlay.closed'), ''],
          { 'font-scale': 0.75, 'text-font': ['literal', ['DIN Pro Regular', 'Arial Unicode MS Regular']] },
          ' \u24D8', { 'font-scale': 0.7, 'text-font': ['literal', ['DIN Pro Regular', 'Arial Unicode MS Regular']] },
        ],
        'text-size': ['interpolate', ['linear'], ['zoom'], 11, 10, 14, 13],
        'text-max-width': 7,
        'text-anchor': 'center',
        'text-allow-overlap': false,
        'symbol-placement': 'point',
        'text-optional': true,
        'text-padding': 24,
      },
      paint: {
        'text-color': ['match', ['get', 'access'],
          'open', '#1B5E20', 'fee', '#0D47A1', 'permit', '#BF360C',
          'limited', '#4A148C', 'restricted', '#BF360C', 'closed', '#B71C1C',
          '#424242'],
        'text-halo-color': 'rgba(255,255,255,0.9)',
        'text-halo-width': 1.5,
        'text-opacity': ['interpolate', ['linear'], ['zoom'], 11, 0.6, 13, 1.0],
      },
    } as any,
    MapLayerCategory.Overlays,
    { z: 26 }
  );

  // Register click + cursor handlers (skip on planner — interferes with route editing)
  if (!_isPlannerPage()) {
    _registerInteractionHandlers();
  }

  console.log('[LandAccess] All 7 layers added. Layer IDs on map:', ALL_LAND_ACCESS_LAYER_IDS.map(id => `${id}: ${!!window.map!.getLayer(id)}`));
}

// ── Remove all layers + source ──────────────────────────────────────────────

export function removeLandAccess(): void {
  _removeInteractionHandlers();
  _closePopup();
  _removePopupStyles();

  ALL_LAND_ACCESS_LAYER_IDS.forEach(id => {
    window.layerRegistry?.removeLayer(id);
  });

  // Reset filter state
  _visibleTypes.clear();
  Array.from(['open', 'fee', 'permit', 'limited', 'restricted', 'closed', 'unknown']).forEach(t => _visibleTypes.add(t));
  _showSeasonal = true;
  _showHunting = true;
}

// ── Toggle visibility by access type ────────────────────────────────────────

export function setAccessTypeVisible(accessType: string, visible: boolean): void {
  if (visible) {
    _visibleTypes.add(accessType);
  } else {
    _visibleTypes.delete(accessType);
  }
  _applyAccessFilter();
}

export function getVisibleAccessTypes(): string[] {
  return Array.from(_visibleTypes);
}

export function setSeasonalVisible(visible: boolean): void {
  _showSeasonal = visible;
  _applyAccessFilter();
}

export function isSeasonalVisible(): boolean {
  return _showSeasonal;
}

export function setHuntingVisible(visible: boolean): void {
  _showHunting = visible;
  _applyAccessFilter();
}

export function isHuntingVisible(): boolean {
  return _showHunting;
}

// ── Canvas pattern creation ─────────────────────────────────────────────────

function _createPatternImageData(
  size: number,
  strokeStyle: string,
  lineWidth: number
): { width: number; height: number; data: Uint8Array } {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, size, size);
  ctx.strokeStyle = strokeStyle;
  ctx.lineWidth = lineWidth;
  ctx.beginPath();
  // Diagonal stripes (45°) — three lines for seamless tiling
  ctx.moveTo(0, size); ctx.lineTo(size, 0);
  ctx.moveTo(-size / 2, size / 2); ctx.lineTo(size / 2, -size / 2);
  ctx.moveTo(size / 2, size + size / 2); ctx.lineTo(size + size / 2, size / 2);
  ctx.stroke();
  const imageData = ctx.getImageData(0, 0, size, size);
  return { width: size, height: size, data: new Uint8Array(imageData.data.buffer) };
}

function _addCanvasPatterns(): void {
  const map = window.map;
  if (!map) return;

  // Seasonal diagonal stripe pattern (brown, 16px tile)
  if (!map.hasImage('seasonal-stripe')) {
    map.addImage('seasonal-stripe', _createPatternImageData(16, 'rgba(121,85,72,0.55)', 1.5), { pixelRatio: 2 });
  }

  // Hunting land hatch pattern (orange, 12px tile)
  if (!map.hasImage('hunting-hatch')) {
    map.addImage('hunting-hatch', _createPatternImageData(12, '#E65100', 2.5), { pixelRatio: 2 });
  }
}

// ── Click / popup handler ───────────────────────────────────────────────────

function _registerInteractionHandlers(): void {
  const map = window.map;
  if (!map) return;

  // Click on fill polygons and labels — not just labels
  const clickableLayers = [LAYER_ID, `${LAYER_ID}-seasonal`, `${LAYER_ID}-labels`];

  clickableLayers.forEach(layerId => {
    // A hazard marker in the area opens alone (skipOnHazard).
    const handler = skipOnHazard(() => map, (e: any) => {
      // Skip if measurement mode is active
      if ((window as any).mainMapMeasurementMode) return;

      // Skip if the click also hit a POI — let the POI handler take priority
      const poiFeatures = map.queryRenderedFeatures(e.point, { layers: ['poi-layer'] });
      if (poiFeatures && poiFeatures.length > 0) return;

      _onLandClick(e);
    });
    map.on('click', layerId, handler);
    _clickHandlers.push({ layer: layerId, handler });

    const enter = () => {
      if ((window as any).mainMapMeasurementMode) return;
      map.getCanvas().style.cursor = 'pointer';
    };
    const leave = () => { map.getCanvas().style.cursor = ''; };
    map.on('mouseenter', layerId, enter);
    map.on('mouseleave', layerId, leave);
    _cursorHandlers.push({ layer: layerId, enter, leave });
  });
}

export function reregisterInteractionHandlers(): void {
  if (_isPlannerPage()) return;
  _removeInteractionHandlers();
  if (window.map && ALL_LAND_ACCESS_LAYER_IDS.some(id => window.map!.getLayer(id))) {
    _registerInteractionHandlers();
  }
}

function _removeInteractionHandlers(): void {
  const map = window.map;
  if (!map) return;

  _clickHandlers.forEach(({ layer, handler }) => {
    map.off('click', layer, handler);
  });
  _clickHandlers.length = 0;

  _cursorHandlers.forEach(({ layer, enter, leave }) => {
    map.off('mouseenter', layer, enter);
    map.off('mouseleave', layer, leave);
  });
  _cursorHandlers.length = 0;
}

function _closePopup(): void {
  if (_popupInstance) {
    _popupInstance.remove();
    _popupInstance = null;
  }
}

function _onLandClick(e: any): void {
  if (!e.features || e.features.length === 0) return;
  const props = e.features[0].properties;

  const accessLabels: Record<string, string> = {
    open:       t('base_map_dialog.land_access_overlay.open_access'),
    fee:        t('base_map_dialog.land_access_overlay.fee_area'),
    permit:     t('base_map_dialog.land_access_overlay.permit_required'),
    limited:    t('base_map_dialog.land_access_overlay.limited_access'),
    restricted: t('base_map_dialog.land_access_overlay.restricted_access'),
    closed:     t('base_map_dialog.land_access_overlay.closed'),
    unknown:    t('base_map_dialog.land_access_overlay.access_unknown'),
  };

  const accessColors: Record<string, string> = {
    open:       '#4CAF50',
    fee:        '#2196F3',
    permit:     '#FF9800',
    limited:    '#AB47BC',
    restricted: '#FF5722',
    closed:     '#F44336',
    unknown:    '#9E9E9E',
  };

  const access = props.access || 'unknown';
  const badgeColor = accessColors[access] || '#9E9E9E';
  const badgeLabel = accessLabels[access] || t('base_map_dialog.land_access_overlay.unknown');

  // Build badges
  let badges = `<span class="la-badge" style="background:${badgeColor}">${badgeLabel}</span>`;
  if (props.hunting_land) {
    badges += '<span class="la-badge" style="background:#E65100">' + t('base_map_dialog.land_access_overlay.hunting_area') + '</span>';
  }

  // Fee info
  let feeHtml = '';
  if (props.fee_required) {
    const feeText = props.fee_amount || t('base_map_dialog.land_access_overlay.fee_required');
    feeHtml = `<div class="la-info-row"><span class="la-icon">&#x1F4B0;</span> ${t('base_map_dialog.land_access_overlay.entry_fee_amount', { fee: _escapeHtml(feeText) })}</div>`;
  }

  // Permit info
  let permitHtml = '';
  if (props.permit_required) {
    const permitLink = props.permit_url
      ? `<a href="${_escapeHtml(props.permit_url)}" target="_blank" rel="noopener" class="la-permit-link">${t('base_map_dialog.land_access_overlay.get_permit')} &rarr;</a>`
      : '';
    permitHtml = `<div class="la-info-row"><span class="la-icon">&#x1F4CB;</span> ${t('base_map_dialog.land_access_overlay.permit_required_sentence')} ${permitLink}</div>`;
  }

  // Seasonal info
  let seasonHtml = '';
  if (props.is_seasonal) {
    let seasonText = t('base_map_dialog.land_access_overlay.seasonal_access');
    if (props.season_open && props.season_close) {
      seasonText = `${_escapeHtml(props.season_open)} &ndash; ${_escapeHtml(props.season_close)}`;
    } else if (props.season_open) {
      seasonText = t('base_map_dialog.land_access_overlay.opens', { date: _escapeHtml(props.season_open) });
    } else if (props.season_close) {
      seasonText = t('base_map_dialog.land_access_overlay.closes', { date: _escapeHtml(props.season_close) });
    }
    seasonHtml = `<div class="la-info-row"><span class="la-icon">&#x1F4C5;</span> ${seasonText}</div>`;
  }

  // GAP description for limited/restricted
  let gapHtml = '';
  if (props.gap_description && (access === 'limited' || access === 'restricted')) {
    gapHtml = `<div class="la-gap-detail">${_escapeHtml(props.gap_description)}</div>`;
  }

  // Info link
  let infoHtml = '';
  if (props.info_url) {
    infoHtml = `<a href="${_escapeHtml(props.info_url)}" target="_blank" rel="noopener" class="la-info-link">${t('base_map_dialog.land_access_overlay.official_info')} &rarr;</a>`;
  }

  const html = `
    <div class="la-popup">
      <div class="la-popup-header">
        <div class="la-popup-name">${_escapeHtml(props.name || t('base_map_dialog.land_access_overlay.unknown_area'))}</div>
        <div class="la-popup-meta">${_escapeHtml(props.land_type || '')}${props.state ? ' &middot; ' + _escapeHtml(props.state) : ''}${props.manager ? ' &middot; ' + _escapeHtml(props.manager) : ''}</div>
      </div>
      <div class="la-badges">${badges}</div>
      ${feeHtml}${permitHtml}${seasonHtml}${gapHtml}${infoHtml}
      <div class="la-popup-footer">PAD-US 4.1${props.last_updated ? ' &middot; ' + _escapeHtml(props.last_updated) : ''}</div>
    </div>
  `;

  _closePopup();

  _popupInstance = new mapboxgl.Popup({ maxWidth: '340px', className: 'hiiker-land-popup' })
    .setLngLat(e.lngLat)
    .setHTML(html)
    .addTo(window.map!);
}

function _escapeHtml(str: string): string {
  if (!str) return '';
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// ── Filter logic ────────────────────────────────────────────────────────────

function _combine(...parts: (any | null)[]): any | null {
  const active = parts.filter(Boolean);
  if (active.length === 0) return null;
  if (active.length === 1) return active[0];
  return ['all', ...active];
}

function _applyAccessFilter(): void {
  const map = window.map;
  if (!map) return;

  const types = Array.from(_visibleTypes);
  const typeFilter = types.length === 7
    ? null // all types visible — skip filter for performance
    : ['in', ['get', 'access'], ['literal', types]];

  const HIDE = ['==', 'nonexistent', 'hide']; // impossible filter

  // Non-seasonal fill
  if (map.getLayer(LAYER_ID)) {
    const base = _showSeasonal
      ? _combine(typeFilter, ['!=', ['get', 'is_seasonal'], 1])
      : (typeFilter || ['has', 'access']);
    map.setFilter(LAYER_ID, base);
  }

  // Seasonal fill + pattern
  if (map.getLayer(`${LAYER_ID}-seasonal`)) {
    map.setFilter(`${LAYER_ID}-seasonal`,
      _showSeasonal ? _combine(typeFilter, ['==', ['get', 'is_seasonal'], 1]) : HIDE);
  }
  if (map.getLayer(`${LAYER_ID}-seasonal-pattern`)) {
    map.setFilter(`${LAYER_ID}-seasonal-pattern`,
      _showSeasonal ? _combine(typeFilter, ['==', ['get', 'is_seasonal'], 1]) : HIDE);
  }

  // Hunting layers
  if (map.getLayer(`${LAYER_ID}-hunting-pattern`)) {
    map.setFilter(`${LAYER_ID}-hunting-pattern`,
      _showHunting ? _combine(typeFilter, ['==', ['get', 'hunting_land'], 1]) : HIDE);
  }
  if (map.getLayer(`${LAYER_ID}-hunting-outline`)) {
    map.setFilter(`${LAYER_ID}-hunting-outline`,
      _showHunting ? _combine(typeFilter, ['==', ['get', 'hunting_land'], 1]) : HIDE);
  }

  // Outline
  if (map.getLayer(`${LAYER_ID}-outline`)) {
    const base = _showHunting
      ? _combine(typeFilter, ['!=', ['get', 'hunting_land'], 1])
      : (typeFilter || ['has', 'access']);
    map.setFilter(`${LAYER_ID}-outline`, base);
  }

  // Labels
  if (map.getLayer(`${LAYER_ID}-labels`)) {
    map.setFilter(`${LAYER_ID}-labels`, typeFilter || ['has', 'access']);
  }
}
